import { Path } from '@vimeo/iris/components';
import { Header, Link, Paragraph } from '@vimeo/iris/typography';

import GuideWrapper from 'src/_components/GuideWrapper';
import Meta from 'src/_components/Meta';

export default function Help() {
  return (
    <>
      <Meta
        title="Vimeo API Help"
        description="If you have questions or problems with the Vimeo API, look no further than this curated, annotated, massively hyperlinked anti-vexation resource."
      />
      <GuideWrapper
        breadcrumbs={
          <Path>
            <Path.Link href={'/'}>Home</Path.Link>
            <Path.Link href={'/api/guides/start'}>API</Path.Link>
            <Path.Current>Help</Path.Current>
          </Path>
        }
        nav={{ page: 'help' }}
      >
        <Header>Getting help</Header>

        <Paragraph size="2">When it comes to questions, there's no rate limit.</Paragraph>

        <Header size="2" id="not-sure-where-to-start">
          Not sure where to start?
        </Header>

        <Paragraph size="2">
          Chances are good that the developer site has the information that you're looking for. All
          you need to do is find it. We have a starting place for just about any situation:
        </Paragraph>

        <ul format="unordered">
          <li>
            <strong>Are you new to Vimeo, the Vimeo API, or both?</strong> Check out{' '}
            <Link variant="minimal" href="/api/guides/start">
              Getting Started
            </Link>
            .
          </li>
          <li>
            <strong>Are you ready to upload videos?</strong> Check out{' '}
            <Link variant="minimal" href="/api/upload/videos">
              Working with Video Uploads
            </Link>
            .
          </li>
          <li>
            <strong>Are you managing authenticated users, access tokens, and scopes?</strong> Check
            out{' '}
            <Link variant="minimal" href="/api/authentication">
              Working with Authentication
            </Link>
            .
          </li>
          <li>
            <strong>Do you want to optimize requests for the Vimeo API?</strong> Check out{' '}
            <Link variant="minimal" href="/api/common-formats">
              Using Common Formats and Parameters
            </Link>
            .
          </li>
          <li>
            <strong>Are you running out of requests per minute?</strong> Check out{' '}
            <Link variant="minimal" href="/guidelines/rate-limiting">
              Working with Rate Limits
            </Link>
            .
          </li>
          <li>
            <strong>
              Are you looking for a complete, task-based technical reference to the entire Vimeo
              API?
            </strong>{' '}
            Check out{' '}
            <Link variant="minimal" href="/api/reference">
              Using the API Reference
            </Link>
            .
          </li>
        </ul>

        <Header size="2" id="need-to-crowdsource-a-problem">
          Need to crowdsource a problem?
        </Header>

        <Paragraph size="2">
          You've been through every guide on this site. But your programming task is...unique, shall
          we say. The sort of thing that people don't make guides about. What's a
          think-outside-the-boxer to do?
        </Paragraph>

        <Paragraph size="2">
          <Link variant="minimal" href="https://stackoverflow.com/tags/vimeo">
            Ask the community of Vimeo API experts on Stack Overflow.
          </Link>{' '}
          Search the stacks to see if someone has already asked a similar question, and, if not, hit
          us up. Tag your question with <code>vimeo-api</code> for issues related to the API,
          vimeo-player for issues related to the <code>player</code>, or just plain{' '}
          <code>vimeo</code> if you aren't sure.
        </Paragraph>

        <Paragraph size="2">
          All we ask is that you be the best version of you. It's a no-drama community. High signal.
          Low noise. People are eager to help, but a little patience never hurts. And if you bump
          into one of our Vimeo staffers, be sure to give them a good word.
        </Paragraph>

        <Header size="2" id="need-to-get-in-touch">
          Need to get in touch?
        </Header>

        <Paragraph size="2">
          Maybe you're still not clear about how something works. Maybe it's not working like we say
          that it does. Maybe it's not working at all. Or maybe it's working beautifully, better
          than you ever thought that it would. Maybe you're a better person because of it, and you
          just want to let us know.
        </Paragraph>

        <Paragraph size="2">
          We never will unless you tell us. There is live, knowledgeable, responsive, compassionate,
          friendly, good-natured human intelligence at the other end of this link, so use it.
        </Paragraph>

        <Paragraph size="2">
          <Link variant="minimal" href="https://vimeo.com/help/contact">
            Contact us about anything.
          </Link>
        </Paragraph>
      </GuideWrapper>
    </>
  );
}
